// 竞技 Classic Daily:服务器权威 + 乐观更新。答案本地不可知,每次猜测走 /api/guess。
import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { useAccount } from '../account';
import { byCca3, type Country } from '../lib/countries';
import { gameNumber, todayKey } from '../lib/game';
import { setModeState, recordWin } from '../lib/storage';
import { getGame, postGuess, type ServerClue } from '../lib/api';
import { celebrate } from '../lib/confetti';
import GuessInput from '../components/GuessInput';
import Legend from '../components/Legend';
import ShareBar from '../components/ShareBar';
import { ClassicBoard, type Row, type Verdict } from './classicBoard';
import { GuessSkeleton } from '../components/Skeleton';
import CountryAnswerCard from '../components/CountryAnswerCard';

function toVerdict(clue: ServerClue): Verdict {
  return {
    isHit: clue.hit,
    regionHit: clue.region,
    pop: clue.population,
    area: clue.area,
    borders: clue.borders,
    gdpPc: clue.gdpPc,
    temp: clue.temp,
    distKm: clue.distanceKm,
    bearing: clue.bearing,
    distColor: clue.distanceColor,
  };
}

interface Item {
  country: Country;
  verdict: Verdict;
}

export default function ClassicCompetitive({ onOffline }: { onOffline: () => void }) {
  const { t } = useI18n();
  const { me, refresh } = useAccount();
  const day = todayKey();

  const [items, setItems] = useState<Item[]>([]);
  const [status, setStatus] = useState<'playing' | 'won'>('playing');
  const [answer, setAnswer] = useState<Country | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [xp, setXp] = useState<{ gained: number; level: number; leveledUp: boolean } | null>(null);
  const guessed = useRef<Set<string>>(new Set());
  const busy = useRef(false);

  useEffect(() => {
    let on = true;
    getGame(day)
      .then((g) => {
        if (!on) return;
        const its: Item[] = [];
        for (const x of g.guesses) {
          const c = byCca3(x.cca3);
          if (c) {
            its.push({ country: c, verdict: toVerdict(x.clue) });
            guessed.current.add(x.cca3);
          }
        }
        setItems(its);
        setStatus(g.status);
        if (g.answerCca3) setAnswer(byCca3(g.answerCca3) ?? null);
        setLoaded(true);
      })
      .catch(() => on && onOffline());
    return () => {
      on = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  async function guess(c: Country) {
    if (status !== 'playing' || busy.current) return;
    if (guessed.current.has(c.cca3)) return;
    guessed.current.add(c.cca3);
    setError(false);
    busy.current = true;
    // 乐观:先占位(shimmer),输入框已清空可继续
    const pending: Verdict = {
      isHit: false,
      regionHit: false,
      pop: { hit: false, higher: false },
      area: { hit: false, higher: false },
      borders: { hit: false, higher: false },
      gdpPc: { hit: false, higher: false },
      temp: { hit: false, higher: false },
      distKm: 0,
      bearing: 0,
      distColor: 'transparent',
      pending: true,
    };
    setItems((prev) => [...prev, { country: c, verdict: pending }]);
    try {
      const resp = await postGuess(day, c.cca3);
      if (resp.guess) {
        const v = toVerdict(resp.guess.clue);
        setItems((prev) => prev.map((it) => (it.country.cca3 === c.cca3 ? { country: c, verdict: v } : it)));
      }
      if (resp.status === 'won') {
        setStatus('won');
        const a = resp.answerCca3 ? byCca3(resp.answerCca3) ?? c : c;
        setAnswer(a);
        if (resp.xp) setXp(resp.xp);
        celebrate();
        // 同步本地态:点亮 DailyStrip 进度/连胜,并触发账号刷新对齐服务端。
        setModeState('classic', day, {
          guesses: [...guessed.current],
          status: 'won',
        });
        recordWin(day);
        window.dispatchEvent(new Event('geodle:progress'));
        refresh();
      }
    } catch {
      // 失败:回滚乐观行,允许重试
      guessed.current.delete(c.cca3);
      setItems((prev) => prev.filter((it) => it.country.cca3 !== c.cca3));
      setError(true);
    } finally {
      busy.current = false;
    }
  }

  if (!loaded) return <GuessSkeleton />;

  const rows: Row[] = items.map((it, i) => ({ ...it, last: i === items.length - 1 }));
  const num = gameNumber(day);
  const streak = me?.streak?.current ?? 0;

  return (
    <div>
      {status === 'playing' && <GuessInput onGuess={guess} />}
      {error && <p className="net-error">{t.netError}</p>}

      <ClassicBoard rows={rows} />

      {items.length > 0 && <Legend numbers />}
      {items.length === 0 && status === 'playing' && <p className="empty-hint">{t.emptyHint}</p>}

      {status === 'won' && answer && (
        <div className="banner win">
          <h2>{t.won}</h2>
          <div className="banner-score">{items.length}/∞</div>
          {xp && xp.gained > 0 && (
            <div className={`xp-gain${xp.leveledUp ? ' levelup' : ''}`}>
              {xp.leveledUp && (
                <span className="xp-levelup">
                  {t.levelUp} {t.account.level} {xp.level}
                </span>
              )}
              <span className="xp-plus">+{xp.gained} {t.xp}</span>
            </div>
          )}
          <CountryAnswerCard country={answer} />
          <ShareBar
            mode="classic"
            num={num}
            guesses={items.map((it) => it.country)}
            answer={answer}
            streak={streak}
          />
        </div>
      )}
    </div>
  );
}
